import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../api/AuthContext.js';
import { fetchAllFutureEventsByOwner } from '../api/fetchAllEvents.js';
import { fetchPastSessions } from '../api/fetchPastSessions.js';
import { getSubscriptionStatus } from '../api/subscriptions.js';
import EventItemAdmin from '../components/EventItemAdmin.js';
import PastSessionItemAdmin from '../components/PastSessionItemAdmin.js';
import SubscriptionBanner from '../components/SubscriptionBanner.js';

function Dashboard() {
    const auth = useAuth();

    const [events, setEvents] = useState(null);
    const [pastEvents, setPastEvents] = useState(null);
    const [subscriptionData, setSubscriptionData] = useState(null);

    // Get all events.
    const getAllEvents = async (e) => {
        const eventData = await fetchAllFutureEventsByOwner(
            auth.currentUser.user_id
        );
        setEvents(eventData);
    };

    useEffect(() => {
        getAllEvents();
    }, []);

    // Get all past events.
    const getPastEvents = async (e) => {
        const sessionData = await fetchPastSessions(auth.currentUser.user_id);
        setPastEvents(sessionData);
    };

    useEffect(() => {
        getPastEvents();
    }, []);

    // Get subscription status
    const fetchSubscription = async () => {
        try {
            const res = await getSubscriptionStatus();
            setSubscriptionData(res);
        } catch (err) {
            console.error('Error fetching subscription:', err);
        }
    };

    useEffect(() => {
        fetchSubscription();
    }, []);

    const navigate = useNavigate();

    const handleAdd = () => {
        navigate('/admin/events/create');
    };

    // Determine if user has any events
    const hasEvents = events && events.length > 0;
    const isLoading = events === null;

    return (
        <>
            <h1>Events</h1>
            <br />

            {/* Subscription status banner - only show if user has events or is at limit */}
            {subscriptionData && hasEvents && (
                <SubscriptionBanner
                    subscriptionData={subscriptionData}
                    context="general"
                />
            )}

            {/* Empty state - no events yet */}
            {!isLoading && !hasEvents && subscriptionData && (
                <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    background: '#f9fafb',
                    borderRadius: '8px',
                    marginBottom: '20px'
                }}>
                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎉</div>
                    <h2 style={{ marginBottom: '16px', color: '#1f2937' }}>
                        Welcome to ReEvent!
                    </h2>
                    <p style={{
                        fontSize: '16px',
                        color: '#6b7280',
                        marginBottom: '24px',
                        maxWidth: '500px',
                        margin: '0 auto 24px'
                    }}>
                        You haven't created any events yet. Get started by creating your <strong>first event for free</strong> - no credit card required!
                    </p>

                    {subscriptionData.subscription_status !== 'active' && (
                        <div style={{
                            background: '#fff',
                            padding: '20px',
                            borderRadius: '8px',
                            marginBottom: '24px',
                            maxWidth: '500px',
                            margin: '0 auto 24px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <p style={{ marginBottom: '12px', color: '#374151' }}>
                                <strong>✨ Your Free Tier includes:</strong>
                            </p>
                            <ul style={{
                                textAlign: 'left',
                                display: 'inline-block',
                                color: '#6b7280',
                                lineHeight: '1.8'
                            }}>
                                <li>1 free event (perfect for trying out ReEvent)</li>
                                <li>Unlimited participants</li>
                                <li>All core features</li>
                            </ul>
                            <p style={{
                                marginTop: '16px',
                                fontSize: '14px',
                                color: '#9ca3af'
                            }}>
                                Need more events? Upgrade to Premium for just $9/month
                            </p>
                        </div>
                    )}

                    <button
                        id="buttonAddEvent"
                        className="button is-primary"
                        onClick={handleAdd}
                        style={{ fontSize: '16px', padding: '12px 32px' }}
                    >
                        🚀 Create Your First Event
                    </button>
                </div>
            )}

            {/* Events table */}
            {isLoading && <div>Loading...</div>}

            {!isLoading && hasEvents && (
                <>
                    <div style={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        marginBottom: '16px'
                    }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse'
                        }}>
                            <thead>
                                <tr style={{
                                    backgroundColor: '#f9fafb',
                                    borderBottom: '2px solid #e5e7eb'
                                }}>
                                    <th style={{
                                        padding: '8px 12px',
                                        textAlign: 'left',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        minWidth: '200px'
                                    }}>Event</th>
                                    <th style={{
                                        padding: '8px 12px',
                                        textAlign: 'center',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        width: '80px'
                                    }}>Status</th>
                                    <th style={{
                                        padding: '8px 12px',
                                        textAlign: 'left',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        minWidth: '180px'
                                    }}>Next Session</th>
                                    <th style={{
                                        padding: '8px 12px',
                                        textAlign: 'left',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em'
                                    }}>Actions</th>
                                    <th style={{
                                        padding: '8px 12px',
                                        textAlign: 'center',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        color: '#6b7280',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        width: '80px'
                                    }}>View</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((event) => {
                                    return (
                                        <EventItemAdmin
                                            key={event.event_id}
                                            event={event}
                                            refreshEvents={getAllEvents}
                                        />
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <button
                        id="buttonAddEvent"
                        className="button is-primary is-small"
                        onClick={handleAdd}
                    >
                        + Create new
                    </button>
                </>
            )}
            <br />
            <br />

            <h1 style={{ marginBottom: '16px' }}>Past Events</h1>

            {pastEvents === null ? (
                <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#6b7280'
                }}>Loading...</div>
            ) : pastEvents.length === 0 ? (
                <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                }}>
                    <div style={{ fontSize: '32px', marginBottom: '12px' }}>📅</div>
                    <p style={{ color: '#6b7280', fontSize: '14px' }}>
                        No past events yet
                    </p>
                </div>
            ) : (
                <div style={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    overflow: 'hidden'
                }}>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse'
                    }}>
                        <thead>
                            <tr style={{
                                backgroundColor: '#f9fafb',
                                borderBottom: '2px solid #e5e7eb'
                            }}>
                                <th style={{
                                    padding: '6px 12px',
                                    textAlign: 'left',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}>Event Details</th>
                                <th style={{
                                    padding: '6px 12px',
                                    textAlign: 'right',
                                    fontSize: '10px',
                                    fontWeight: '600',
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    width: '120px'
                                }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pastEvents.map((session) => (
                                <PastSessionItemAdmin
                                    key={session.session_id}
                                    session={session}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}

export default Dashboard;
